;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();import get from "lodash/get"
import { isOrgLeaderRole } from "../create-profile/isOrgLeaderRole"
import { parseOrgLegalCompliancePrivateMetadata } from "../organization/orgLegalCompliancePrivateMetadata"
import type { OrgAgreementsSignedMap } from "../organization/orgAgreementsStatus"

/** Accepted teammates (beyond the first member, assumed to be the leader) plus pending invites must reach this to complete foundation. */
export const MIN_FOUNDATION_TEAM_GROWTH = 1

export type OrganizationInvitationForSetup = {
  revokedAt?: string | null
  acceptedAt?: string | null
  expiresAt?: string | null
}

/**
 * Pending = not revoked and not accepted (same filter as team-management).
 * If the connection is paginated, pass all fetched nodes; counts may be a lower bound.
 */
export function countPendingOrganizationInvitations(
  invitations: OrganizationInvitationForSetup[] | null | undefined,
): number {
  if (!invitations?.length) {
    return 0
  }
  return invitations.filter((inv) => !inv.revokedAt && !inv.acceptedAt).length
}

/** Serializable snapshot for debugging why org foundation (team) shows completed or not. */
export function getOrganizationFoundationTeamDebug(params: {
  organization?: any
  organizationMembers?: any[] | null
  organizationMemberTotalCount?: number | null
  organizationInvitations?: OrganizationInvitationForSetup[] | null
}): {
  orgPublished: boolean
  publishedAt: string | null | undefined
  hasPublicProfile: boolean
  memberCountUsed: number
  organizationMemberTotalCount: number | null | undefined
  membersEdgesLength: number
  acceptedOthers: number
  invitationNodesFetched: number
  pendingInviteCount: number
  teamGrowthCount: number
  minTeamGrowthRequired: number
  teamGrowthSatisfied: boolean
  foundationStepCompleteIfPublished: boolean
  /** First few invites — check revokedAt/acceptedAt if counts look wrong */
  invitationPreview: Array<{
    revokedAt?: string | null
    acceptedAt?: string | null
    expiresAt?: string | null
  }>
} {
  const orgPublished = Boolean(
    params.organization?.publishedAt && params.organization?.publicProfile,
  )
  const memberCountUsed =
    params.organizationMemberTotalCount ?? params.organizationMembers?.length ?? 0
  const acceptedOthers = Math.max(0, memberCountUsed - 1)
  const pendingInviteCount = countPendingOrganizationInvitations(
    params.organizationInvitations,
  )
  const teamGrowthCount = acceptedOthers + pendingInviteCount
  const teamGrowthSatisfied = teamGrowthCount >= MIN_FOUNDATION_TEAM_GROWTH
  const invitations = params.organizationInvitations ?? []
  const preview = invitations.slice(0, 8).map((inv) => ({
    revokedAt: inv?.revokedAt ?? null,
    acceptedAt: inv?.acceptedAt ?? null,
    expiresAt: inv?.expiresAt ?? null,
  }))

  return {
    orgPublished,
    publishedAt: params.organization?.publishedAt,
    hasPublicProfile: Boolean(params.organization?.publicProfile),
    memberCountUsed,
    organizationMemberTotalCount: params.organizationMemberTotalCount,
    membersEdgesLength: params.organizationMembers?.length ?? 0,
    acceptedOthers,
    invitationNodesFetched: invitations.length,
    pendingInviteCount,
    teamGrowthCount,
    minTeamGrowthRequired: MIN_FOUNDATION_TEAM_GROWTH,
    teamGrowthSatisfied,
    foundationStepCompleteIfPublished: orgPublished && teamGrowthSatisfied,
    invitationPreview: preview,
  }
}

/**
 * Computes the setup state for organization onboarding steps
 * @param {Object} params - Parameters for computing setup state
 * @param {Object} params.organization - The organization object
 * @param {Object} params.onboardingStatus - The onboarding status object (provider-level)
 * @param {Object} params.group - The group object (optional)
 * @param {Object} params.provider - The provider object (optional)
 * @param {String} params.providerId - The provider ID (optional)
 * @param {Array} params.organizationMembers - Array of organization members (optional)
 * @param {Number} params.organizationMemberTotalCount - listOrganizationMembers.totalCount when available (optional)
 * @param {Array} params.organizationInvitations - Outgoing org invitations (optional; used for pending count)
 * @param {Object} params.organizationSubscription - Organization subscription object (optional)
 *
 * @returns {Object} - The setup state for each organization step
 */
export type ComputeOrganizationSetupStateParams = {
  organization?: any
  onboardingStatus?: any
  group?: any
  provider?: any
  providerId?: any
  organizationMembers?: any[]
  organizationMemberTotalCount?: number | null
  organizationInvitations?: OrganizationInvitationForSetup[] | null
  organizationSubscription?: any
  roleInOrg?: string | null
  orgHasPublishedGroups?: boolean
  /** Org-level compliance completion (separate from personal BAAs). */
  orgComplianceComplete?: boolean
}

function hasNonEmptyString(value: unknown): boolean {
  return typeof value === "string" && value.trim() !== ""
}

/**
 * Shared completion check for `/onboarding/org-legal-and-compliance`.
 * Mirrors the wizard requirements:
 * 1) Basic org info: legal name + state of organization
 * 2) Contacts and address: business/notice address + privacy + security contact
 * 3) Agreements: electronic signature consent + signer confirmation + BAA accepted
 */
export function isOrganizationLegalComplianceComplete(
  organization?: {
    privateMetadata?: unknown
  } | null,
): boolean {
  const parsed = parseOrgLegalCompliancePrivateMetadata(
    organization?.privateMetadata,
  )

  const hasBasicInfo =
    hasNonEmptyString(parsed.legalName) &&
    hasNonEmptyString(parsed.stateOfOrganization)

  const hasBusinessNoticeAddress =
    hasNonEmptyString(parsed.businessNoticeAddressLine1) &&
    hasNonEmptyString(parsed.businessNoticeAddressCity) &&
    hasNonEmptyString(parsed.businessNoticeAddressState) &&
    hasNonEmptyString(parsed.businessNoticeAddressPostalCode) &&
    hasNonEmptyString(parsed.businessNoticeAddressCountry)

  const hasPrivacyContact =
    hasNonEmptyString(parsed.privacyContactName) &&
    hasNonEmptyString(parsed.privacyContactTitle) &&
    hasNonEmptyString(parsed.privacyContactEmail)

  const hasSecurityContact =
    parsed.privacySecuritySamePerson === true
      ? hasPrivacyContact
      : hasNonEmptyString(parsed.securityContactName) &&
        hasNonEmptyString(parsed.securityContactTitle) &&
        hasNonEmptyString(parsed.securityContactEmail)

  const hasRequiredAgreements =
    parsed.electronicSignatureAgreement === true &&
    parsed.authorizedSignerConfirmed === true &&
    parsed.baaAccepted === true

  return (
    hasBasicInfo &&
    hasBusinessNoticeAddress &&
    hasPrivacyContact &&
    hasSecurityContact &&
    hasRequiredAgreements
  )
}

/**
 * Same required-fields check as `isOrganizationLegalComplianceComplete`, but
 * the agreement requirement is satisfied by real signatures from
 * `listOrganizationAgreements` (ELECTRONIC_CONSENT + BAA) rather than the
 * legacy `privateMetadata` booleans.
 */
export function isOrganizationLegalComplianceCompleteWithAgreements(
  organization:
    | {
        privateMetadata?: unknown
      }
    | null
    | undefined,
  signed: OrgAgreementsSignedMap,
): boolean {
  const parsed = parseOrgLegalCompliancePrivateMetadata(
    organization?.privateMetadata,
  )

  const hasBasicInfo =
    hasNonEmptyString(parsed.legalName) &&
    hasNonEmptyString(parsed.stateOfOrganization)

  const hasBusinessNoticeAddress =
    hasNonEmptyString(parsed.businessNoticeAddressLine1) &&
    hasNonEmptyString(parsed.businessNoticeAddressCity) &&
    hasNonEmptyString(parsed.businessNoticeAddressState) &&
    hasNonEmptyString(parsed.businessNoticeAddressPostalCode) &&
    hasNonEmptyString(parsed.businessNoticeAddressCountry)

  const hasPrivacyContact =
    hasNonEmptyString(parsed.privacyContactName) &&
    hasNonEmptyString(parsed.privacyContactTitle) &&
    hasNonEmptyString(parsed.privacyContactEmail)

  const hasSecurityContact =
    parsed.privacySecuritySamePerson === true
      ? hasPrivacyContact
      : hasNonEmptyString(parsed.securityContactName) &&
        hasNonEmptyString(parsed.securityContactTitle) &&
        hasNonEmptyString(parsed.securityContactEmail)

  const hasSignedAgreements = signed.electronicConsent && signed.baa

  return (
    hasBasicInfo &&
    hasBusinessNoticeAddress &&
    hasPrivacyContact &&
    hasSecurityContact &&
    hasSignedAgreements
  )
}

export type OrganizationSetupSubstepCompletion = {
  foundation: {
    publishOrganizationPage: boolean
    inviteTeamMembers: boolean
  }
  profile: {
    createProfile: boolean
    personalCompliance: boolean
  }
  services: {
    configureGroups: boolean
  }
}

function deriveProfileState(
  onboardingStatus: any,
  providerId: any,
): "not_started" | "in_progress" | "completed" {
  const profileComplete = get(onboardingStatus, "profileComplete", false)
  const profilePendingPublication = get(
    onboardingStatus,
    "profilePendingPublication",
    false,
  )
  const baaHipaatizerComplete = get(onboardingStatus, "baaHipaatizerComplete", false)
  const baaZestComplete = get(onboardingStatus, "baaZestComplete", false)
  const hasDocumentsSigned = baaHipaatizerComplete && baaZestComplete

  if ((profileComplete || profilePendingPublication) && hasDocumentsSigned) {
    return "completed"
  }
  if (providerId) {
    return "in_progress"
  }
  return "not_started"
}

/** Per sub-step completion for Organization Foundation, Profile, and Services cards (leader dashboard UI). */
export function getOrganizationSetupSubstepCompletion(
  params: ComputeOrganizationSetupStateParams,
): OrganizationSetupSubstepCompletion {
  const {
    organization,
    onboardingStatus,
    organizationMembers = [],
    organizationMemberTotalCount,
    organizationInvitations,
  } = params

  const publishOrganizationPage = Boolean(
    organization?.publishedAt && organization?.publicProfile,
  )
  const memberCount =
    organizationMemberTotalCount ?? organizationMembers?.length ?? 0
  const acceptedOthers = Math.max(0, memberCount - 1)
  const pendingInviteCount = countPendingOrganizationInvitations(
    organizationInvitations,
  )
  const teamGrowthCount = acceptedOthers + pendingInviteCount
  const inviteTeamMembers = teamGrowthCount >= MIN_FOUNDATION_TEAM_GROWTH

  // Profile substeps
  const profileComplete = get(onboardingStatus, "profileComplete", false)
  const profilePendingPublication = get(
    onboardingStatus,
    "profilePendingPublication",
    false,
  )
  const createProfile = Boolean(profileComplete || profilePendingPublication)
  const baaHipaatizerComplete = get(onboardingStatus, "baaHipaatizerComplete", false)
  const baaZestComplete = get(onboardingStatus, "baaZestComplete", false)
  const personalCompliance = Boolean(baaHipaatizerComplete && baaZestComplete)

  // Services substeps
  const groupComplete = get(onboardingStatus, "groupComplete", false)
  const groupPendingPublication = get(
    onboardingStatus,
    "groupPendingPublication",
    false,
  )
  const configureGroups = Boolean(groupComplete || groupPendingPublication)

  return {
    foundation: {
      publishOrganizationPage,
      inviteTeamMembers,
    },
    profile: {
      createProfile,
      personalCompliance,
    },
    services: {
      configureGroups,
    },
  }
}

export function computeOrganizationSetupState({
  organization,
  onboardingStatus,
  group,
  provider,
  providerId,
  organizationMembers = [],
  organizationMemberTotalCount,
  organizationInvitations,
  organizationSubscription,
  roleInOrg,
  orgHasPublishedGroups,
  orgComplianceComplete,
}: ComputeOrganizationSetupStateParams) {
  const isLeader = isOrgLeaderRole(roleInOrg)

  // Step 1: Organization Foundation
  // - Organization published (publicProfile exists and publishedAt is set)
  // - Team growth: other members + valid pending invites >= MIN_FOUNDATION_TEAM_GROWTH
  const orgPublished = organization?.publishedAt && organization?.publicProfile
  const memberCount =
    organizationMemberTotalCount ?? organizationMembers?.length ?? 0
  const acceptedOthers = Math.max(0, memberCount - 1)
  const pendingInviteCount = countPendingOrganizationInvitations(
    organizationInvitations,
  )
  const teamGrowthCount = acceptedOthers + pendingInviteCount
  const teamGrowthSatisfied = teamGrowthCount >= MIN_FOUNDATION_TEAM_GROWTH
  const orgFoundationComplete = orgPublished && teamGrowthSatisfied
  const orgFoundationInProgress = orgPublished || teamGrowthCount > 0

  let orgFoundationState = "not_started"
  if (orgFoundationComplete) {
    orgFoundationState = "completed"
  } else if (orgFoundationInProgress) {
    orgFoundationState = "in_progress"
  }

  // Step 2: Professional Profile (same as existing profile logic)
  const profileState = deriveProfileState(onboardingStatus, providerId)

  // Step 3: Services & Communication — create group listings
  // Complete when 1+ group is complete or pending publication
  const groupComplete = get(onboardingStatus, "groupComplete", false)
  const groupPendingPublication = get(
    onboardingStatus,
    "groupPendingPublication",
    false,
  )
  const latestGroupId = get(onboardingStatus, "latestGroupId")
  const hasGroup = group && (group.group || group.id)

  const servicesComplete = Boolean(groupComplete || groupPendingPublication)
  const servicesInProgress = Boolean(latestGroupId || hasGroup)

  let servicesState = "not_started"
  if (servicesComplete) {
    servicesState = "completed"
  } else if (servicesInProgress) {
    servicesState = "in_progress"
  }

  // Build setup state — steps vary by org role
  const setupState: Record<string, string> = {
    organizationFoundation: orgFoundationState,
    profile: profileState,
  }

  // Facilitators skip services step if the org already has published groups
  const skipServices = !isLeader && orgHasPublishedGroups
  if (!skipServices) {
    setupState.services = servicesState
  }

  // Only leaders see compliance and plan steps
  if (isLeader) {
    // Step 4: Compliance & Security — org-level compliance
    // Uses /onboarding/org-legal-and-compliance flow with its own mutation/query
    // TODO: Wire orgComplianceComplete to org compliance query when available
    const resolvedOrgComplianceComplete =
      typeof orgComplianceComplete === "boolean"
        ? orgComplianceComplete
        : isOrganizationLegalComplianceComplete(organization)

    let complianceState = "not_started"
    if (resolvedOrgComplianceComplete) {
      complianceState = "completed"
    }
    setupState.compliance = complianceState

    // Step 5: Choose Your Plan
    const planComplete = !!organizationSubscription
    let planState = "not_started"
    if (planComplete) {
      planState = "completed"
    } else if (orgFoundationState === "completed") {
      planState = "in_progress"
    }
    setupState.plan = planState
  }

  return setupState
}

/** @deprecated Use getTotalOrganizationListingSteps() for role-aware count. */
export const TOTAL_ORGANIZATION_LISTING_STEPS = 5

/** Role-aware total step count for org onboarding. */
export function getTotalOrganizationListingSteps(
  roleInOrg?: string | null,
  orgHasPublishedGroups?: boolean,
): number {
  if (!isOrgLeaderRole(roleInOrg)) {
    // Facilitator: foundation + profile + services (or just foundation + profile)
    return orgHasPublishedGroups ? 2 : 3
  }
  return 5
}

/**
 * Counts org steps not yet completed (same semantics as OrganizationOnboardingStepsSection).
 */
export function countRemainingOrganizationSteps(
  setupState: ReturnType<typeof computeOrganizationSetupState> | null | undefined,
): number {
  if (!setupState) {
    return 0
  }
  return Object.values(setupState).filter((s) => s !== "completed").length
}

/**
 * Determines which organization step is next based on the setup state
 * @param {Object} setupState - The setup state for each organization step
 * @returns {String|null} - The ID of the next step or null if all steps are complete
 */
export const getNextOrganizationStepId = (setupState) => {
  const stepOrder = [
    "organizationFoundation",
    "profile",
    "services",
    "compliance",
    "plan",
  ]
  for (const step of stepOrder) {
    if (step in setupState && setupState[step] !== "completed") {
      return step
    }
  }
  return null
}

/**
 * Gets the appropriate URL for each organization step based on completion status
 * @param {Object} params - Parameters for determining URLs
 * @param {Object} params.organization - The organization object
 * @param {Object} params.onboardingStatus - The onboarding status object
 * @param {Object} params.group - The group object (optional)
 * @param {Object} params.me - The user object (optional)
 * @param {Object} params.provider - The provider object (optional)
 * @param {String} params.providerId - The provider ID (optional)
 * @param {String} params.orgId - The organization ID
 * @param {Object} params.organizationSubscription - Organization subscription object (optional)
 *
 * @returns {Object} - URLs for each organization step
 */
export const getOrganizationStepUrls = ({
  organization,
  onboardingStatus,
  group,
  me,
  provider,
  providerId,
  orgId,
  organizationSubscription,
  roleInOrg,
  orgHasPublishedGroups,
}: {
  organization?: any
  onboardingStatus?: any
  group?: any
  me?: any
  provider?: any
  providerId?: any
  orgId?: any
  organizationSubscription?: any
  roleInOrg?: string | null
  orgHasPublishedGroups?: boolean
}) => {
  const isLeader = isOrgLeaderRole(roleInOrg)
  const latestGroupId = onboardingStatus?.latestGroupId
  const orgPublished = organization?.publishedAt && organization?.publicProfile

  // Step 1: Organization Foundation
  let orgFoundationUrl = `/organization/${orgId}`
  if (orgPublished) {
    orgFoundationUrl = "/organization/team-management"
  }

  // Step 2: Professional Profile (reuse existing logic)
  const isProfileCreated = providerId && onboardingStatus
  const isProfileComplete =
    onboardingStatus?.profileComplete || onboardingStatus?.profilePendingPublication
  const isDocumentsComplete =
    onboardingStatus?.baaHipaatizerComplete && onboardingStatus?.baaZestComplete

  let profileUrl = "/create-profile"
  if (isProfileCreated && isProfileComplete && isDocumentsComplete) {
    profileUrl = `/group-leader/${providerId}`
  } else if (isProfileCreated && !isProfileComplete) {
    profileUrl = "/onboarding"
  } else if (isProfileCreated && !isDocumentsComplete) {
    profileUrl = "/onboarding/personal-compliance"
  } else if (providerId && !isProfileCreated) {
    profileUrl = "/create-profile"
  }

  const stepUrls: Record<string, { url: string }> = {
    organizationFoundation: { url: orgFoundationUrl },
    profile: { url: profileUrl },
  }

  // Services step — skipped for facilitators when org has groups
  const skipServices = !isLeader && orgHasPublishedGroups
  if (!skipServices) {
    let servicesUrl = "/create-group?onboarding=true"
    if (latestGroupId) {
      const groupPendingPublication = onboardingStatus?.groupPendingPublication
      const isGroupComplete = onboardingStatus?.groupComplete

      if (isGroupComplete || groupPendingPublication) {
        servicesUrl = `/group/${latestGroupId}?preview=true`
      } else {
        servicesUrl = `/create-group?group_id=${latestGroupId}&onboarding=true`
      }
    }
    stepUrls.services = { url: servicesUrl }
  }

  // Compliance & Plan — leaders only
  if (isLeader) {
    stepUrls.compliance = {
      url: "/onboarding/org-legal-and-compliance",
    }

    let planUrl = "/subscription?orgType=organization"
    if (organizationSubscription) {
      planUrl = "/account/manage-subscription"
    }
    stepUrls.plan = { url: planUrl }
  }

  return stepUrls
}

/** `/organization/:orgId` base path from org foundation step URLs (including `/organization/team-management`). */
export function getOrganizationPageBasePath(
  orgFoundationStepUrl: string,
  orgIdForFallback?: string | null,
): string {
  if (
    orgFoundationStepUrl === "/organization/team-management" ||
    orgFoundationStepUrl.startsWith("/organization/team-management?")
  ) {
    if (orgIdForFallback != null && String(orgIdForFallback).trim() !== "") {
      return `/organization/${String(orgIdForFallback).trim()}`
    }
    return "/organization/team-management"
  }
  const m = orgFoundationStepUrl.match(/^(\/organization\/[^/?]+)/)
  return m ? m[1] : orgFoundationStepUrl
}

/** Team management lives at a single app route; active org comes from session. */
export function getOrganizationTeamManagementUrl(
  _orgFoundationStepUrl: string,
): string {
  return "/organization/team-management"
}
